import {
  type OrderStatusReason,
  OrderStatusReasonOrderDescIdTooLow,
  OrderStatusReasonUnspecified,
  type RequestID,
} from '@/api/spec.ts';
import { useHeartbeatStore } from '@/common/heartbeatStore.ts';
import type { MarketData } from '@/common/MarketData.ts';
import type { OrderFormData } from '@/feature/order_entry/order_schema.ts';
import { type InFlightRequest, type OrderPostingState, useOpenOrdersStore } from '../OpenOrdersStore.ts';
import { getOrderErrorInfo } from './orderErrorHandling.ts';

/** Used when a market's config omits the field. Generous on purpose — a short window re-sends. */
export const FALLBACK_RETRY_BLOCKS = 30;
export const FALLBACK_TTL_BLOCKS = 20;

/** Past this much lag the client wasn't watching when the window passed, so it can't re-send. */
export const MAX_RETRY_LAG_BLOCKS = 30;

/**
 * Automatic re-sends per user action, counted by `InFlightRequest.retryCount`. One, and the budget
 * is shared with the `OrderDescIdTooLow` re-submit — both read the same counter.
 */
export const MAX_ORDER_RETRIES = 1;

/** Acknowledged but unresolved: the order is probably resting, so don't say "try again". */
export const ORDER_STATUS_UNKNOWN_MESSAGE = 'Order status unknown. Please check your Orders tab.';

/**
 * `order_retry_blocks` — not `order_ttl_blocks` — is the exchange's own answer to "how long until a
 * re-send is safe when the outcome is unknown", and it runs LONGER (live config: 22 vs 20).
 */
export function armRequestWindow(requestId: RequestID, market: MarketData | undefined) {
  setRequestWindow(requestId, market?.orderRetryBlocks || FALLBACK_RETRY_BLOCKS, true);
}

/**
 * Must run after `patchOrder`, which retires the request if this update decided its outcome — so
 * membership in `inFlightRequests` answers "still unresolved?". `Pending` and a cancel-and-replace's
 * old entity both leave it unresolved, and disarming there dropped postings onto the 30s backstop.
 *
 * Re-armed from now, not from submit: the order exists, so the question is whether it runs its
 * execution window without another word. Any update proves it landed — `canRetry` goes off for good.
 */
export function trackRequestUpdate(requestId: RequestID, market: MarketData | undefined) {
  if (!useOpenOrdersStore.getState().inFlightRequests.has(requestId)) return;
  setRequestWindow(requestId, market?.orderTtlBlocks || FALLBACK_TTL_BLOCKS, false);
}

/**
 * Armed from `lastSequenceNumber`, NOT `headBlockNumber`, because that is what the sweep compares
 * against — `indexingLagBlocks` is exactly `head - sn`, so arming from the head stretched every
 * deadline by the current lag, worst during the congestion it exists for. With no `sn` the request
 * is left unarmed and only the backstop can judge it.
 */
function setRequestWindow(requestId: RequestID, blocks: number, canRetry: boolean) {
  const currentSn = useHeartbeatStore.getState().trading.lastSequenceNumber;
  useOpenOrdersStore.getState().armRequest(requestId, currentSn == null ? null : currentSn + blocks, canRetry);
}

export type TimeoutAction =
  | { kind: 'retry'; orderData: OrderFormData; uiKey: string }
  /** `landed` separates "acknowledged but unresolved" from "no answer at all" in Sentry. */
  | { kind: 'fail'; message: string; landed: boolean };

/** What the sweep should do about one in-flight request right now. */
export type SweepAction =
  | { kind: 'wait' }
  | { kind: 'retry'; orderData: OrderFormData; uiKey: string }
  /** `via` picks the Sentry channel: the two answer different questions about the same silence. */
  | { kind: 'fail'; message: string; landed: boolean; via: 'deadline' | 'backstop' };

/** The toast reason when the stream went quiet before anything acknowledged the request. */
export const ORDER_CONNECTION_LOST_MESSAGE = 'Order timed out — connection may be lost';

/**
 * The whole sweep decision for one request, pure — no store reads. Precedence matters: the block
 * deadline is judged first because it is the precise answer and the only one that can say "retry";
 * the backstop speaks only for a request the block path cannot reach (never armed, or the stream
 * stopped advancing).
 */
export function decideRequestAction(
  request: Pick<InFlightRequest, 'orderData' | 'uiKey' | 'retryCount' | 'canRetry' | 'deadlineBlock' | 'backstopAt'>,
  currentSn: number | null,
  now: number
): SweepAction {
  const { deadlineBlock } = request;

  if (deadlineBlock != null && currentSn != null && currentSn >= deadlineBlock) {
    const decision = decideTimeoutAction({ ...request, deadlineBlock }, currentSn);
    return decision.kind === 'retry' ? decision : { ...decision, via: 'deadline' };
  }

  if (now < request.backstopAt) return { kind: 'wait' };

  // `canRetry: false` means the exchange acknowledged this request, so blaming the connection would
  // be wrong — the order is very likely resting.
  const landed = !request.canRetry;
  return {
    kind: 'fail',
    message: landed ? ORDER_STATUS_UNKNOWN_MESSAGE : ORDER_CONNECTION_LOST_MESSAGE,
    landed,
    via: 'backstop',
  };
}

/** The armed shape: only a request whose deadline has actually passed reaches this. */
export type ExpiredRequest = Pick<InFlightRequest, 'orderData' | 'uiKey' | 'retryCount' | 'canRetry'> & {
  deadlineBlock: number;
};

export function decideTimeoutAction(request: ExpiredRequest, currentSn: number): TimeoutAction {
  const { orderData, uiKey, retryCount, canRetry, deadlineBlock } = request;
  // The market has moved on, so re-sending is worse than reporting it.
  const tooLateToRetry = currentSn - deadlineBlock > MAX_RETRY_LAG_BLOCKS;

  if (canRetry && !tooLateToRetry && orderData && orderData.marketId != null && uiKey && retryCount < MAX_ORDER_RETRIES) {
    return { kind: 'retry', orderData, uiKey };
  }

  if (!canRetry) {
    return { kind: 'fail', message: ORDER_STATUS_UNKNOWN_MESSAGE, landed: true };
  }
  if (!orderData) {
    return { kind: 'fail', message: 'Order cancellation timed out', landed: false };
  }
  if (tooLateToRetry) {
    return { kind: 'fail', message: 'Order confirmation timed out', landed: false };
  }
  return { kind: 'fail', message: 'Order confirmation timed out after retry', landed: false };
}

/**
 * `Account.lfr` is a high-water mark — `max(known, request_id)` over every request the account
 * makes. So `lfr >= rq` proves receipt **only for the highest outstanding id**; for a partially-lost
 * burst it reads true for ids that never arrived. Used only to turn `canRetry` off, never to settle:
 * "stop re-sending this" is sound, "it worked" is not.
 */
export function applyForwardedRequestProof(lastForwardedRequestId: RequestID | undefined) {
  if (lastForwardedRequestId == null) return;

  const { inFlightRequests } = useOpenOrdersStore.getState();
  let highestId: RequestID | undefined;
  let highest: InFlightRequest | undefined;
  for (const [requestId, request] of inFlightRequests) {
    if (highestId == null || requestId > highestId) {
      highestId = requestId;
      highest = request;
    }
  }

  if (highestId == null || !highest?.canRetry || highestId > lastForwardedRequestId) return;
  useOpenOrdersStore.getState().armRequest(highestId, highest.deadlineBlock, false);
}

/**
 * The exchange loses every in-flight request when the socket closes, silently and with no status
 * for any of them, and the protocol's instruction is to treat that as a failure of all of them.
 * Wording still follows what we know: a request whose `canRetry` is already off reached the
 * exchange, so it is reported as unresolved rather than blamed on the connection.
 */
export function failInFlightRequestsOnDisconnect(): { total: number; landed: number } {
  const requests = [...useOpenOrdersStore.getState().inFlightRequests];
  let landedCount = 0;

  for (const [requestId, request] of requests) {
    const landed = !request.canRetry;
    if (landed) landedCount++;
    const message = landed
      ? ORDER_STATUS_UNKNOWN_MESSAGE
      : getOrderErrorInfo(OrderStatusReasonUnspecified, ORDER_CONNECTION_LOST_MESSAGE).message;
    useOpenOrdersStore.getState().failInFlightRequest(requestId, OrderStatusReasonUnspecified, message);
  }

  return { total: requests.length, landed: landedCount };
}

export function shouldAutoResetPosting(
  posting: Pick<OrderPostingState, 'status' | 'error' | 'retryCount'>,
  inFlightCount: number
): boolean {
  if (posting.status !== 'failed' || inFlightCount !== 0) return false;
  // OrderDescIdTooLow is handled by the dedicated retry effect while the budget lasts; once spent,
  // fall through and reset.
  if (posting.error === OrderStatusReasonOrderDescIdTooLow && posting.retryCount < MAX_ORDER_RETRIES) return false;
  return true;
}

export { OrderStatusReasonOrderDescIdTooLow };
export type { OrderStatusReason };
